import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { OsLotReview } from '../../lib/types/os-lot-reviews';

import { OsLotReviewProposedTelepacList } from './os-lot-review-telepac';
import classes from './os-lot-reviews.module.css';

function formatProposedValue(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return String(value);
}

type OsLotReviewCorrectionSummaryProps = {
  review: OsLotReview;
  showTelepacFiles: boolean;
};

export function OsLotReviewCorrectionSummary({
  review,
  showTelepacFiles,
}: OsLotReviewCorrectionSummaryProps) {
  const { t } = useTranslation();

  const hasProposedMetrics =
    review.proposed_volume_reel != null ||
    review.proposed_rdt_reel != null ||
    review.proposed_area_reel != null ||
    Boolean(review.proposed_declared_telepac_id?.trim());
  const hasTelepacFiles = (review.proposed_telepacs?.length ?? 0) > 0;

  if (
    review.status !== 'correction_proposed' &&
    !hasProposedMetrics &&
    !hasTelepacFiles &&
    !review.comment?.trim()
  ) {
    return null;
  }

  return (
    <div className={classes.correctionSummary}>
      <p className={classes.correctionSummaryTitle}>
        {t('osLotReviews.correction.title')}
      </p>
      <Stack gap={2}>
        <Text className={classes.correctionSummaryLabel} size="sm">
          {t('osLotReviews.form.comment')}
        </Text>
        <Text className={classes.correctionSummaryValue} size="sm">
          {review.comment?.trim() || '—'}
        </Text>
      </Stack>
      {hasProposedMetrics ? (
        <div className={classes.correctionSummaryGrid}>
          <div>
            <Text className={classes.correctionSummaryLabel} size="sm">
              {t('osLotReviews.form.proposed_volume')}
            </Text>
            <Text className={classes.correctionSummaryValue} size="sm">
              {formatProposedValue(review.proposed_volume_reel)}
            </Text>
          </div>
          <div>
            <Text className={classes.correctionSummaryLabel} size="sm">
              {t('osLotReviews.form.proposed_yield')}
            </Text>
            <Text className={classes.correctionSummaryValue} size="sm">
              {formatProposedValue(review.proposed_rdt_reel)}
            </Text>
          </div>
          <div>
            <Text className={classes.correctionSummaryLabel} size="sm">
              {t('osLotReviews.form.proposed_area')}
            </Text>
            <Text className={classes.correctionSummaryValue} size="sm">
              {formatProposedValue(review.proposed_area_reel)}
            </Text>
          </div>
          {review.proposed_declared_telepac_id ? (
            <div>
              <Text className={classes.correctionSummaryLabel} size="sm">
                {t('osLotReviews.link.telepac')}
              </Text>
              <Text className={classes.correctionSummaryValue} size="sm">
                {review.proposed_declared_telepac_id}
              </Text>
            </div>
          ) : null}
        </div>
      ) : null}
      {showTelepacFiles && hasTelepacFiles ? (
        <OsLotReviewProposedTelepacList files={review.proposed_telepacs} />
      ) : null}
    </div>
  );
}
